import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCompany, useMoneyFmt } from "@/lib/company";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, Upload, FileText, Sparkles, ArrowRight,
  CalendarDays, Building2, Plus, ChevronRight, X, AlertTriangle, CircleAlert,
} from "lucide-react";
import MonthCloseBreadcrumb from "@/components/MonthCloseBreadcrumb";

// Reconciliation — Slice R1+R2+R3.
// R1 (Plaid auto-clear) runs invisibly on every sync + on-demand via the
// "Auto-clear settled Plaid txns" button on this page. R2 (manual matching)
// is the checkable list on the left with a live difference readout. R3
// (statement-PDF fuzzy matching) is behind the "Match statement PDF" upload.
export default function Reconciliation() {
  const fmtMoney = useMoneyFmt();
  const { currentId } = useCompany();
  const [banks, setBanks] = useState([]);
  const [acctId, setAcctId] = useState("");
  // Freeform date range — pro reconciles anything from a single week to a
  // multi-year backfill. On Finish, the backend splits the range into ONE
  // reconciliation doc per calendar month so the history table stays
  // per-month even for large imports.
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [periodEnd, setPeriodEnd] = useState(() => {
    const d = new Date(); d.setDate(0);  // last day of previous month
    return d.toISOString().slice(0, 10);
  });
  const asOf = periodEnd;
  const [openBal, setOpenBal] = useState("");
  const [closeBal, setCloseBal] = useState("");
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [matchResult, setMatchResult] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const fileRef = useRef(null);
  const [urlParams] = useSearchParams();

  // Deep-link from Month Close: /accounting/reconciliation?month=YYYY-MM
  // pre-fills the "Start reconciliation" form with that month's bounds and
  // opens it, AND narrows the history table to any recons overlapping it.
  const monthFilter = urlParams.get("month") || "";
  const monthBounds = useMemo(() => {
    const m = /^(\d{4})-(\d{2})$/.exec(monthFilter);
    if (!m) return null;
    const y = Number(m[1]), mo = Number(m[2]);
    if (mo < 1 || mo > 12) return null;
    const last = new Date(y, mo, 0).getUTCDate();
    return {
      start: `${m[1]}-${m[2]}-01`,
      end:   `${m[1]}-${m[2]}-${String(last).padStart(2, "0")}`,
    };
  }, [monthFilter]);

  // Hydrate the start-new form + open it once when arriving with ?month=.
  // When landing from the Cockpit with ?month=YYYY-MM we show the roster
  // ONLY — the Start form and history table stay hidden until the pro
  // picks an account via a Reconcile button on the roster. That turns
  // the page into a clean two-step: pick an account → reconcile it.
  useEffect(() => {
    if (!monthBounds) return;
    setPeriodStart(monthBounds.start);
    setPeriodEnd(monthBounds.end);
    // Intentionally do NOT setStartOpen(true) here — pro must click a
    // row's Reconcile button first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter]);

  // Roster-mode: landing from ?month= with no account chosen yet →
  // hide everything except the roster. First Reconcile click flips it.
  const [rosterMode, setRosterMode] = useState(Boolean(monthFilter));
  useEffect(() => { setRosterMode(Boolean(monthFilter)); }, [monthFilter]);

  // Per-account roster for the month (powers the one-click "Reconcile"
  // table that appears at the top when ?month= is in the URL). Only
  // fetched when a month filter is active.
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const loadRoster = async () => {
    if (!currentId || !monthFilter) { setRoster(null); return; }
    setRosterLoading(true);
    try {
      const r = await api.get(
        `/companies/${currentId}/reconciliations/month-roster`,
        { params: { month: monthFilter } },
      );
      setRoster(r.data);
    } catch (e) {
      // Non-fatal — hide the roster if the endpoint errors.
      setRoster(null);
    } finally {
      setRosterLoading(false);
    }
  };
  useEffect(() => { loadRoster(); /* eslint-disable-next-line */ }, [currentId, monthFilter]);

  // One-click: pre-fill the Start form for a specific account+month and
  // scroll the form into view. Called from the roster's "Reconcile" button.
  const startAccountReconcile = (accountId) => {
    setAcctId(accountId);
    if (monthBounds) {
      setPeriodStart(monthBounds.start);
      setPeriodEnd(monthBounds.end);
    }
    setOpenBal("");
    setCloseBal("");
    setStartOpen(true);
    // Exit roster-mode so the Start form + history become visible.
    setRosterMode(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="recon-start-new"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  // Account filter for the history table. Populated dynamically from
  // the loaded reconciliations so the dropdown ONLY lists accounts that
  // actually have at least one reconciliation attached — no clutter from
  // banks the pro hasn't reconciled yet.
  const [filterAcctId, setFilterAcctId] = useState("");
  const historyAccountOptions = useMemo(() => {
    const seen = new Map();
    for (const r of history) {
      const id = r.bank_account_id || r.account_id || "";
      if (!id || seen.has(id)) continue;
      seen.set(id, {
        id,
        name: r.account_name || "—",
        last4: r.account_last4 || "",
      });
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [history]);

  // Status filter — driven by `?filter=unreconciled` (from the To Do
  // sidebar's Reconcile card, or any other deep-link source). When
  // set, hides "reconciled" and "qbo_covered" rows so the CPA only
  // sees the work that still needs doing. State reflects the URL and
  // can be cleared inline via the filter chip we render above the
  // table.
  const urlStatusFilter = urlParams.get("filter") || "";
  const [statusFilter, setStatusFilter] = useState(
    urlStatusFilter === "unreconciled" ? "unreconciled" : "",
  );
  // Keep in sync if the query param changes mid-session (e.g. user
  // navigates back with the browser Back button).
  useEffect(() => {
    setStatusFilter(urlStatusFilter === "unreconciled" ? "unreconciled" : "");
  }, [urlStatusFilter]);
  // Auto-expand the "Start reconciliation" form when the filter is on
  // — the primary reason a CPA lands here with `?filter=unreconciled`
  // is to actually knock work out, not to browse history.
  useEffect(() => {
    if (statusFilter === "unreconciled") setStartOpen(true);
  }, [statusFilter]);

  // History table is scoped to (a) the requested month when arriving via
  // Month Close deep-link, and (b) the account picked from the filter
  // dropdown above the table. Both scopes stack.
  const visibleHistory = useMemo(() => {
    let rows = history;
    if (monthBounds) {
      rows = rows.filter(r => {
        const s = r.period_start || r.as_of;
        const e = r.period_end || r.as_of;
        if (!s || !e) return false;
        return s <= monthBounds.end && e >= monthBounds.start;
      });
    }
    if (filterAcctId) {
      rows = rows.filter(r => (r.bank_account_id || r.account_id) === filterAcctId);
    }
    if (statusFilter === "unreconciled") {
      // Anything not "cleanly done" — includes variance rows, in-progress
      // attempts, and never-completed drafts. Mirrors the "Variance /
      // open / auto" badges rendered below (Reconciliation.jsx:640-692).
      rows = rows.filter(r =>
        r.status !== "reconciled" && r.status !== "qbo_covered"
      );
    }
    return rows;
  }, [history, monthBounds, filterAcctId, statusFilter]);

  // Load bank/CC accounts + past reconciliations once we know the company.
  const load = async () => {
    if (!currentId) return;
    const [a, r] = await Promise.all([
      api.get(`/companies/${currentId}/accounts`),
      api.get(`/companies/${currentId}/reconciliations`),
    ]);
    const asset = (a.data.accounts || []).filter(
      x => x.type === "asset" || x.type === "liability"
    );
    setBanks(asset);
    // Deep-link ?bank_account_id=... wins over "first account" default.
    const deepLink = urlParams.get("bank_account_id");
    if (asset.length) {
      if (deepLink && asset.some(b => b.id === deepLink)) {
        setAcctId(deepLink);
      } else if (!acctId) {
        setAcctId(asset[0].id);
      }
    }
    setHistory(r.data.reconciliations || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentId]);

  // Debounced preview refresh whenever the user changes any of the 3 inputs.
  useEffect(() => {
    if (!currentId || !acctId || !asOf) return;
    let cancelled = false;
    setLoading(true);
    setChecked(new Set());
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/companies/${currentId}/reconciliations/preview`, {
          params: {
            bank_account_id: acctId, as_of: asOf,
            opening_balance: parseFloat(openBal) || 0,
            closing_balance: parseFloat(closeBal) || 0,
          },
        });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Preview failed");
      } finally { if (!cancelled) setLoading(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [currentId, acctId, asOf, openBal, closeBal]);

  const clearedSum = useMemo(() => {
    if (!preview) return 0;
    return (preview.uncleared || [])
      .filter(t => checked.has(t.id))
      .reduce((s, t) => s + Number(t.amount || 0), 0);
  }, [preview, checked]);

  // Classic recon math:  closing − opening − sum(cleared) == 0  when done.
  const diffLive = useMemo(() => {
    const op = parseFloat(openBal) || 0;
    const cl = parseFloat(closeBal) || 0;
    return round2(cl - op - clearedSum);
  }, [openBal, closeBal, clearedSum]);
  const isBalanced = Math.abs(diffLive) < 0.005;

  const toggle = (id) => {
    setChecked(c => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const tickAll = () => {
    if (!preview) return;
    setChecked(new Set(preview.uncleared.map(t => t.id)));
  };
  const untickAll = () => setChecked(new Set());

  const finish = async () => {
    if (!isBalanced) return;
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/reconciliations/complete`, {
        bank_account_id: acctId,
        period_start: periodStart,
        period_end: periodEnd,
        opening_balance: parseFloat(openBal) || 0,
        closing_balance: parseFloat(closeBal) || 0,
        cleared_txn_ids: [...checked],
      });
      const n = r.data?.count || 1;
      toast.success(
        n > 1
          ? `Reconciled ${checked.size} transactions across ${n} monthly reports.`
          : `Reconciled ${checked.size} transactions.`
      );
      setOpenBal(""); setCloseBal("");
      setChecked(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finish failed");
    } finally { setBusy(false); }
  };

  // Save an in-progress recon (ticked txns + balances) so the CPA can
  // step away and come back later. Idempotent per (acct, period) — one
  // draft slot per period. Hydrated by the effect below on mount /
  // period switch. Aug 24 2026.
  const saveDraft = async () => {
    if (!currentId) return;
    if (!periodStart || !periodEnd) {
      toast.error("Pick a period first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/reconciliations/draft`, {
        bank_account_id: acctId || null,
        period_start: periodStart,
        period_end: periodEnd,
        opening_balance: openBal === "" ? null : parseFloat(openBal),
        closing_balance: closeBal === "" ? null : parseFloat(closeBal),
        cleared_txn_ids: [...checked],
      });
      toast.success(`Progress saved — ${r.data?.cleared_count || 0} ticked. Pick up any time.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  // Hydrate a saved draft when the CPA opens a period they had paused.
  // Fires whenever the active (acct, period) tuple changes.
  useEffect(() => {
    if (!currentId || !periodStart || !periodEnd) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/companies/${currentId}/reconciliations/draft`, {
          params: {
            bank_account_id: acctId || undefined,
            period_start: periodStart,
            period_end: periodEnd,
          },
        });
        if (cancelled) return;
        const d = r.data?.draft;
        if (!d) return;
        // Only hydrate fields the user hasn't already started editing —
        // otherwise a background auto-load would clobber typed work.
        if (openBal === "" && d.opening_balance !== null && d.opening_balance !== undefined) {
          setOpenBal(String(d.opening_balance));
        }
        if (closeBal === "" && d.closing_balance !== null && d.closing_balance !== undefined) {
          setCloseBal(String(d.closing_balance));
        }
        if (checked.size === 0 && Array.isArray(d.cleared_txn_ids) && d.cleared_txn_ids.length) {
          setChecked(new Set(d.cleared_txn_ids));
          toast.info(`Restored saved progress — ${d.cleared_txn_ids.length} ticked.`);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, acctId, periodStart, periodEnd]);

  const runAutoClear = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/reconciliations/auto-clear`);
      toast.success(`Auto-cleared ${r.data.cleared} Plaid transactions.`);
      // Refresh preview so newly-cleared items disappear from the list.
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-clear failed");
    } finally { setBusy(false); }
  };

  const [bootstrapConfirm, setBootstrapConfirm] = useState(null);
  const runAutoBootstrap = async (overwrite_placeholders = false) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/companies/${currentId}/reconciliations/auto-bootstrap`,
        { overwrite_placeholders },
      );
      const created = r.data?.created?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      const errors = r.data?.errors || [];
      const purged = r.data?.purged || 0;

      // First pass: if nothing created and only reason is "already reconciled"
      // on placeholders, ask the user if they want us to purge and retry.
      const looksLikePlaceholderBlock =
        created === 0 &&
        !overwrite_placeholders &&
        history.some(h => !h.bank_account_id || !h.cleared_txn_ids || h.cleared_txn_ids.length === 0);
      if (looksLikePlaceholderBlock) {
        setBootstrapConfirm({ purge_count: history.filter(h => !h.bank_account_id || !(h.cleared_txn_ids?.length)).length });
        setBusy(false);
        return;
      }

      if (created) {
        const reroutedNote = r.data?.rerouted ? ` (repaired ${r.data.rerouted} mis-routed txn${r.data.rerouted === 1 ? "" : "s"})` : "";
        toast.success(
          purged
            ? `Purged ${purged} placeholder(s), created ${created} real monthly reconciliation${created === 1 ? "" : "s"}.${reroutedNote}`
            : `Created ${created} real monthly reconciliation${created === 1 ? "" : "s"} from the Plaid feed.${reroutedNote}`
        );
      } else if (errors.length) {
        toast.error(errors[0]);
      } else {
        toast.info(`Nothing new to reconcile. ${skipped} period${skipped === 1 ? "" : "s"} already covered.`);
      }
      if (errors.length > 1) {
        // Surface remaining errors so they don't get lost.
        errors.slice(1).forEach(e => toast.warning(e));
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-reconcile failed");
    } finally { setBusy(false); }
  };

  const uploadStatement = async (file) => {
    if (!file || !acctId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bank_account_id", acctId);
      const r = await api.post(
        `/companies/${currentId}/reconciliations/match-statement`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
      );
      // Auto-correct the bank account picker when the OCR resolved the
      // statement to a different existing ledger account than the one
      // selected. Example: user selects "1000 Cash and Bank" but uploads
      // a "1011 Bank of America Checking ···6084" statement — we switch
      // the picker to 1011 so the reconciliation session works against
      // the correct 89 uncleared txns rather than the empty 1000 account.
      if (r.data?.account_overridden && r.data?.resolved_account) {
        setAcctId(r.data.resolved_account.id);
        toast.info(
          `Switched to ${r.data.resolved_account.name} — detected on the statement (${r.data.resolved_account.reason}).`,
          { duration: 6000 },
        );
      }
      // Auto-fill statement dates + opening/ending balances from the OCR
      // so the user doesn't have to re-type numbers already visible on
      // the PDF. Always overwrite — the statement is authoritative for a
      // reconciliation of THIS statement, even if the user pre-typed
      // something before uploading.
      const autoFilled = [];
      if (r.data?.statement_period_start) {
        setPeriodStart(r.data.statement_period_start);
        autoFilled.push("start");
      }
      if (r.data?.statement_period_end) {
        setPeriodEnd(r.data.statement_period_end);
        autoFilled.push("end");
      }
      if (r.data?.statement_opening_balance != null) {
        setOpenBal(String(r.data.statement_opening_balance));
        autoFilled.push("opening");
      }
      if (r.data?.statement_closing_balance != null) {
        setCloseBal(String(r.data.statement_closing_balance));
        autoFilled.push("ending");
      }
      if (autoFilled.length) {
        toast.success(
          `Auto-filled ${autoFilled.join(" · ")} from statement PDF.`,
          { duration: 5000 },
        );
      }
      setMatchResult(r.data);
      setShowMatchModal(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Statement matching failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="reconciliation-page">
      <MonthCloseBreadcrumb />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Reconciliation</h1>
          <p className="text-slate-500 text-sm mt-1">
            {rosterMode
              ? <>Pick an account to reconcile for {monthBounds ? monthBounds.start.slice(0, 7) : ""}.</>
              : monthBounds
                ? <>{visibleHistory.length} reconciliation{visibleHistory.length === 1 ? "" : "s"} in {monthBounds.start.slice(0, 7)} · <b>{history.length} total</b></>
                : <>{history.length} reconciliation period{history.length === 1 ? "" : "s"} · Plaid txns auto-clear after 5 days.</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!rosterMode && !startOpen && (
            <button
              onClick={() => setStartOpen(true)}
              data-testid="recon-start-new-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-slate-900 text-white hover:bg-slate-800"
            >
              <Plus size={13} /> Start reconciliation
            </button>
          )}
        </div>
      </div>

      {/* Per-account month roster — shown when the page is opened with
          ?month=YYYY-MM (deep-link from Cockpit / Month Close). Lists
          every bank/CC account with activity in the month or an overlapping
          reconciliation session, with a one-click Reconcile button on the
          rows that still need work. */}
      {monthBounds && (
        <MonthRosterPanel
          month={monthFilter}
          roster={roster}
          loading={rosterLoading}
          onReconcile={startAccountReconcile}
          selectedAcctId={acctId}
        />
      )}

      {/* Collapsible: start-new interactive matcher */}
      {!rosterMode && startOpen && (
        <div className="rounded-xl border bg-white overflow-hidden" data-testid="recon-start-new">
          <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50">
            <div className="font-heading font-semibold text-sm">New reconciliation</div>
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.csv,image/*"
                onChange={(e) => uploadStatement(e.target.files?.[0])}
                className="hidden"
                data-testid="recon-statement-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy || !acctId}
                data-testid="recon-match-statement-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border hover:bg-slate-50 disabled:opacity-40"
              >
                <Upload size={13} /> Match statement PDF
              </button>
              <button
                onClick={() => setStartOpen(false)}
                data-testid="recon-close-new-btn"
                className="p-1.5 rounded-md hover:bg-slate-100"
                title="Close"
              >
                <X size={14} className="text-slate-500" />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Setup */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-1">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Building2 size={12} /> Bank account
                </div>
                <select
                  value={acctId}
                  onChange={(e) => setAcctId(e.target.value)}
                  className="w-full border rounded-md px-2 py-1.5 text-sm"
                  data-testid="recon-account-select"
                >
                  {banks.map(b => <option key={b.id} value={b.id}>{b.code} {b.name}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <CalendarDays size={12} /> Statement start
                </div>
                <input
                  type="date" value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="w-full border rounded-md px-2 py-1.5 text-sm"
                  data-testid="recon-period-start"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <CalendarDays size={12} /> Statement end
                </div>
                <input
                  type="date" value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="w-full border rounded-md px-2 py-1.5 text-sm"
                  data-testid="recon-period-end"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Opening balance</div>
                <input
                  type="number" step="0.01" value={openBal}
                  onChange={(e) => setOpenBal(e.target.value)}
                  placeholder="e.g. 0.00"
                  className="w-full border rounded-md px-2 py-1.5 text-sm font-mono-num"
                  data-testid="recon-open-balance"
                />
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Ending balance</div>
                <input
                  type="number" step="0.01" value={closeBal}
                  onChange={(e) => setCloseBal(e.target.value)}
                  placeholder="e.g. 12450.31"
                  className="w-full border rounded-md px-2 py-1.5 text-sm font-mono-num"
                  data-testid="recon-close-balance"
                />
              </div>
            </div>
            {spansMultipleMonths(periodStart, periodEnd) && (
              <div className="text-[11px] text-cyan-800 bg-cyan-50 border border-cyan-100 rounded px-3 py-1.5">
                This range spans multiple months. On Finish, we'll create <b>one reconciliation report per month</b> automatically.
              </div>
            )}

            {/* Live scoreboard + list */}
            {preview && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-xl border overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50">
                    <div className="text-xs uppercase tracking-widest text-slate-500">
                      Uncleared through {periodEnd} · {preview.uncleared.length}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <button onClick={tickAll} className="text-cyan-700 hover:underline" data-testid="recon-tick-all">Check all</button>
                      <span className="text-slate-300">·</span>
                      <button onClick={untickAll} className="text-slate-500 hover:underline" data-testid="recon-untick-all">Clear</button>
                    </div>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto">
                    {loading && (
                      <div className="p-8 text-center text-slate-500 text-sm">
                        <Loader2 size={16} className="animate-spin inline mr-2" />Loading…
                      </div>
                    )}
                    {!loading && preview.uncleared.length === 0 && (
                      <div className="p-8 text-center text-slate-500 text-sm">
                        Nothing to clear — everything through {periodEnd} is already reconciled. 🎉
                      </div>
                    )}
                    {!loading && preview.uncleared.map(t => (
                      <label key={t.id}
                        className="flex items-center gap-3 px-4 py-2 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer text-sm"
                        data-testid={`recon-row-${t.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked.has(t.id)}
                          onChange={() => toggle(t.id)}
                          className="accent-slate-900"
                        />
                        <span className="w-24 text-slate-500 font-mono-num text-xs">{t.date}</span>
                        <span className="flex-1 truncate">{t.description}</span>
                        <span className="text-slate-400 text-xs truncate max-w-[160px]">{t.category_account_name}</span>
                        <span className={`font-mono-num tabular-nums w-28 text-right ${Number(t.amount) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                          {fmtMoney(t.amount)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <ScoreboardCard
                  preview={preview} clearedCount={checked.size} clearedSum={clearedSum}
                  opening={parseFloat(openBal) || 0}
                  closing={parseFloat(closeBal) || 0}
                  diff={diffLive} isBalanced={isBalanced}
                  onFinish={async () => { await finish(); setStartOpen(false); }}
                  onSaveDraft={saveDraft}
                  busy={busy}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* History table — primary surface. Hidden when the page is in
          roster-mode (deep-link from Cockpit with ?month=…) so the pro
          sees a clean account picker first. */}
      {!rosterMode && (
      <div className="rounded-xl border bg-white overflow-hidden" data-testid="recon-history">
        {/* Account filter — populated from accounts that actually have a
            reconciliation so the dropdown stays clean. Shown as soon as
            any reconciliations exist so the pro can see the filter
            surface even with a single account. */}
        {historyAccountOptions.length >= 1 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b bg-slate-50/60 text-xs flex-wrap">
            <span className="text-slate-500 uppercase tracking-widest">Filter</span>
            <select
              value={filterAcctId}
              onChange={(e) => setFilterAcctId(e.target.value)}
              className="border rounded-md px-2 py-1 text-xs bg-white"
              data-testid="recon-history-account-filter"
            >
              <option value="">All accounts ({historyAccountOptions.length})</option>
              {historyAccountOptions.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.last4 ? ` ···${a.last4}` : ""}
                </option>
              ))}
            </select>
            {filterAcctId && (
              <button
                onClick={() => setFilterAcctId("")}
                className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                data-testid="recon-history-filter-clear"
                title="Clear filter"
              >
                <X size={11} /> Clear
              </button>
            )}
            {statusFilter === "unreconciled" && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                data-testid="recon-status-filter-chip"
              >
                Status: Unreconciled only
                <button
                  type="button"
                  onClick={() => setStatusFilter("")}
                  className="hover:text-amber-900"
                  data-testid="recon-status-filter-clear"
                  title="Show all statuses"
                >
                  <X size={11} />
                </button>
              </span>
            )}
            <span className="text-slate-400 ml-auto">
              Showing {visibleHistory.length} of {history.length}
            </span>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500 border-b bg-slate-50">
            <tr>
              <th className="px-4 py-2 text-left">Period</th>
              <th className="px-4 py-2 text-left">Account</th>
              <th className="px-4 py-2 text-center">Status</th>
              <th className="px-4 py-2 text-right">Statement</th>
              <th className="px-4 py-2 text-right">Ledger</th>
              <th className="px-4 py-2 text-right">Diff</th>
              <th className="px-4 py-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {visibleHistory.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-500 text-sm">
                {monthBounds
                  ? <>No reconciliations for {monthBounds.start.slice(0, 7)} yet. Use "+ Start reconciliation" — the form is pre-filled with this month's date range.</>
                  : <>No reconciliations yet. Hit "+ Start reconciliation" to begin.</>}
              </td></tr>
            )}
            {visibleHistory.map(r => (
              <tr key={r.id} className="border-b last:border-b-0 hover:bg-slate-50 cursor-pointer" data-testid={`recon-history-row-${r.id}`}>
                <td className="px-4 py-2 font-mono-num text-xs text-cyan-700">
                  <Link to={`/accounting/reconciliation/${r.id}`} className="hover:underline">
                    {formatPeriodLabel(r.period_start, r.period_end, r.as_of)}
                  </Link>
                </td>
                <td className="px-4 py-2">
                  <Link to={`/accounting/reconciliation/${r.id}`} className="hover:underline">
                    {r.account_name || "—"}{r.account_last4 ? ` ···${r.account_last4}` : ""}
                  </Link>
                </td>
                <td className="px-4 py-2 text-center">
                  {(() => {
                    // Feb 2026 — Bug B: for truly Veryfi/Plaid-imported
                    // recons that have a non-zero diff, the old "green
                    // RECONCILED" badge misled CPAs into thinking books
                    // were clean. Now: qbo_covered → indigo "QBO
                    // Verified"; diff > $0.02 → amber "VARIANCE" so
                    // the CPA knows to open a manual reconcile;
                    // otherwise emerald "RECONCILED" as before.
                    const hasDiff = Math.abs(r.diff || 0) >= 0.02;
                    const rebuiltTag = r.auto_rebuilt_at && (
                      <span
                        className="ml-1 text-[9px] text-slate-500"
                        title={`Auto-rebuilt on ${r.auto_rebuilt_at.slice(0,10)} — ${r.auto_rebuilt_drift_count || 0} stale transaction reference(s) were repaired`}
                        data-testid="recon-status-rebuilt-tag"
                      >
                        🔄
                      </span>
                    );
                    if (r.status === "qbo_covered") {
                      return (
                        <span
                          className="inline-flex items-center text-[10px] uppercase px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-semibold"
                          title="QuickBooks already has transactions in this range. Veryfi statement was verified against QBO — no ledger drift."
                          data-testid="recon-status-qbo-covered"
                        >
                          QBO Verified{rebuiltTag}
                        </span>
                      );
                    }
                    if (r.status === "reconciled" && hasDiff) {
                      return (
                        <span
                          className="inline-flex items-center text-[10px] uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
                          title="Statement balance doesn't match ledger. Open the period and reconcile manually or investigate missing/duplicate transactions."
                          data-testid="recon-status-variance"
                        >
                          Variance{rebuiltTag}
                        </span>
                      );
                    }
                    return (
                      <span
                        className={`inline-flex items-center text-[10px] uppercase px-2 py-0.5 rounded ${
                          r.status === "reconciled" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                        }`}
                        data-testid={`recon-status-${r.status || "open"}`}
                      >
                        {r.status || (r.source === "plaid_auto" ? "auto" : "open")}
                        {rebuiltTag}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-2 text-right font-mono-num tabular-nums">{fmtMoney(r.statement_balance || 0)}</td>
                <td className={`px-4 py-2 text-right font-mono-num tabular-nums ${
                  r.status === "qbo_covered" ? "text-slate-400" : ""
                }`}>{fmtMoney(r.ledger_balance || 0)}</td>
                <td className={`px-4 py-2 text-right font-mono-num tabular-nums ${
                  r.status === "qbo_covered"
                    ? "text-slate-400"           // muted — the "diff" is meaningless when QBO covered the period
                    : Math.abs(r.diff || 0) < 0.005 ? "text-emerald-700" : "text-red-700"
                }`}>
                  {r.status === "qbo_covered" ? "—" : fmtMoney(r.diff || 0)}
                </td>
                <td className="px-4 py-2 text-slate-300">
                  <Link to={`/accounting/reconciliation/${r.id}`}><ChevronRight size={14} /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {bootstrapConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setBootstrapConfirm(null)}
          data-testid="bootstrap-confirm-modal"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="font-heading font-semibold text-lg">Replace placeholder reconciliations?</h3>
              <p className="text-sm text-slate-600 mt-1">
                We found <b>{bootstrapConfirm.purge_count}</b> reconciliation{bootstrapConfirm.purge_count === 1 ? "" : "s"} with no bank
                account or no cleared transactions attached — likely demo/seed artifacts. They're blocking auto-reconcile because
                their date ranges overlap the periods we'd generate.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Real completed reconciliations (with an account and cleared transactions) will <b>not</b> be touched.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setBootstrapConfirm(null)}
                className="px-3 py-1.5 text-sm rounded-md border"
                data-testid="bootstrap-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={async () => { setBootstrapConfirm(null); await runAutoBootstrap(true); }}
                className="px-4 py-1.5 text-sm rounded-md bg-cyan-600 text-white hover:bg-cyan-700 inline-flex items-center gap-1.5"
                data-testid="bootstrap-purge-btn"
              >
                <Sparkles size={13} /> Purge placeholders &amp; auto-reconcile
              </button>
            </div>
          </div>
        </div>
      )}

      {showMatchModal && matchResult && (
        <MatchResultModal
          result={matchResult}
          onClose={() => { setShowMatchModal(false); setMatchResult(null); }}
          onApply={(ids) => {
            // Feed matches into the CURRENT reconciliation session instead
            // of globally clearing them. The user is inside "New
            // reconciliation" with opening/ending balances — those matched
            // rows need to count toward `clearedSum` so the difference
            // collapses to $0 and the Finish button unlocks. The Finish
            // flow will then persist `cleared_at` + `cleared_source=manual`
            // + `cleared_reconciliation_id` for a proper audit trail.
            // (Previously we hit `/apply-matches`, which set `cleared_at`
            // out-of-band and left the session's diff untouched — the
            // symptom the user reported.)
            if (!ids.length) {
              setShowMatchModal(false); setMatchResult(null); return;
            }
            setChecked(prev => {
              const n = new Set(prev);
              for (const id of ids) n.add(id);
              return n;
            });
            toast.success(
              `Pre-checked ${ids.length} matched transaction${ids.length === 1 ? "" : "s"}. Review and Finish reconciliation to save.`
            );
            setShowMatchModal(false); setMatchResult(null);
          }}
        />
      )}
    </div>
  );
}

function ScoreboardCard({ preview, clearedCount, clearedSum, opening, closing, diff, isBalanced, onFinish, onSaveDraft, busy }) {

  const fmtMoney = useMoneyFmt();
  return (
    <div className={`rounded-xl border bg-white p-5 space-y-3 ${isBalanced ? "ring-2 ring-emerald-200" : ""}`} data-testid="recon-scoreboard">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Reconciliation summary</div>
      <Row label="Opening balance" value={opening} />
      <Row label="Ending balance"  value={closing} />
      <Row label={`Cleared this session (${clearedCount})`} value={clearedSum} muted />
      <div className="border-t pt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Difference</span>
          <span className={`font-mono-num tabular-nums text-xl font-bold ${
            isBalanced ? "text-emerald-700" : "text-red-700"
          }`} data-testid="recon-difference">
            {fmtMoney(diff)}
          </span>
        </div>
        <div className={`text-xs mt-1 ${isBalanced ? "text-emerald-700" : "text-slate-500"}`}>
          {isBalanced ? "Ready to finish — books match the statement." : "Keep ticking items until the difference is $0.00."}
        </div>
      </div>
      <button
        onClick={onFinish}
        disabled={!isBalanced || busy || clearedCount === 0}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-40"
        data-testid="recon-finish-btn"
      >
        {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
        Finish reconciliation
      </button>
      {/* Save progress lets the CPA step away with everything ticked +
          balances preserved. Restored automatically next time they
          re-open this period. Doesn't require a balanced sheet. */}
      {onSaveDraft && (
        <button
          onClick={onSaveDraft}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40 mt-2"
          data-testid="recon-save-draft-btn"
          title="Save progress and come back later — nothing is finalized until you click Finish"
        >
          Save progress
        </button>
      )}
    </div>
  );
}

function Row({ label, value, muted }) {

  const fmtMoney = useMoneyFmt();
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={muted ? "text-slate-500" : "text-slate-700"}>{label}</span>
      <span className="font-mono-num tabular-nums">{fmtMoney(value)}</span>
    </div>
  );
}

function MatchResultModal({ result, onClose, onApply }) {
  // Modal shown after a Veryfi-OCR statement upload. Groups per-line matches
  // by confidence tier so users can bulk-apply the safe ones and spot-check
  // the fuzzy ones.
  const [pickSuggest, setPickSuggest] = useState(new Set(
    (result.suggest || []).map(s => s.best?.id).filter(Boolean),
  ));
  const [pickAuto] = useState(new Set(
    (result.auto || []).map(s => s.best?.id).filter(Boolean),
  ));
  const toApply = useMemo(() => new Set([...pickAuto, ...pickSuggest]), [pickAuto, pickSuggest]);
  const toggleSuggest = (id) => {
    setPickSuggest(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="recon-match-modal"
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold">Statement match preview</h3>
            <p className="text-xs text-slate-500">
              {result.line_count} statement lines · <b className="text-emerald-700">{result.auto_count} auto</b>
              {" · "}<b className="text-amber-700">{result.suggest_count} suggested</b>
              {" · "}<b className="text-slate-500">{result.manual_count} manual</b>
              {result.missing_from_statement_count > 0 && (
                <> · <b className="text-red-700">{result.missing_from_statement_count} in books but not on statement</b></>
              )}
            </p>
            {result.account_overridden && result.resolved_account && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-cyan-50 text-cyan-900 border border-cyan-200 px-2 py-1 rounded" data-testid="recon-match-account-override">
                <Sparkles size={11} className="text-cyan-700" />
                Auto-switched to <b>{result.resolved_account.name}</b> — {result.resolved_account.reason}.
              </div>
            )}
            {!result.account_overridden && result.auto_count === 0 && result.suggest_count === 0 && result.line_count > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs bg-amber-50 text-amber-900 border border-amber-200 px-2 py-1 rounded" data-testid="recon-match-no-candidates">
                No candidates found in the ledger for this account
                {result.statement_last4 && <> (statement ···{result.statement_last4})</>}.
                Confirm you picked the right bank account, or import the statement first via <b>Connections → Statements</b>.
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-sm">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
          {result.auto?.length > 0 && (
            <Tier
              title={<span className="text-emerald-700">✓ Auto-matched (≥0.90 confidence)</span>}
              rows={result.auto}
              picked={pickAuto}
              locked
            />
          )}
          {result.suggest?.length > 0 && (
            <Tier
              title={<span className="text-amber-700">Suggested (0.60–0.90) — uncheck any that look wrong</span>}
              rows={result.suggest}
              picked={pickSuggest}
              onToggle={toggleSuggest}
            />
          )}
          {result.manual?.length > 0 && (
            <Tier
              title={<span className="text-slate-600">No confident match ({result.manual_count})</span>}
              rows={result.manual}
              manual
            />
          )}
          {result.missing_from_statement?.length > 0 && (
            <MissingTier rows={result.missing_from_statement} />
          )}
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500">{toApply.size} of {result.line_count} will be cleared.</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border">Cancel</button>
            <button
              onClick={() => onApply([...toApply])}
              disabled={toApply.size === 0}
              className="px-4 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="recon-match-apply"
            >
              Apply <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tier({ title, rows, picked, onToggle, locked, manual }) {

  const fmtMoney = useMoneyFmt();
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest mb-2">{title}</div>
      <div className="space-y-1">
        {rows.map((row, idx) => (
          <div key={idx} className={`rounded border px-3 py-2 flex items-center gap-3 ${
            manual ? "bg-red-50/40 border-red-100" : "bg-white"
          }`}>
            {!manual && onToggle ? (
              <input
                type="checkbox"
                checked={picked?.has(row.best?.id)}
                onChange={() => onToggle(row.best?.id)}
                className="accent-slate-900"
              />
            ) : locked ? (
              <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
            ) : (
              <FileText size={14} className="text-slate-400 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="truncate">{row.line?.description || row.line?.merchant}</div>
              <div className="text-[11px] text-slate-500 font-mono-num">
                {row.line?.date} · {fmtMoney(row.line?.amount || 0)}
              </div>
            </div>
            {row.best ? (
              <div className="text-right">
                <div className="text-[11px] text-slate-500">match ↓ ({Math.round((row.score || 0) * 100)}%)</div>
                <div className="text-xs truncate max-w-[220px]">{row.best.description}</div>
                <div className="text-[11px] text-slate-500 font-mono-num">{row.best.date} · {fmtMoney(row.best.amount)}</div>
              </div>
            ) : (
              <div className="text-[11px] text-red-700 italic">No candidate — likely missing from ledger.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MissingTier({ rows }) {
  const fmtMoney = useMoneyFmt();
  // Ledger transactions with no matching line on the statement.
  // For a client using Axiom this usually means either:
  //   1. The transaction was posted to the books but never actually hit the bank
  //      (typo, or the txn was deleted at the bank end)
  //   2. A duplicate posting the client will want to review
  //   3. A fraud item that hit the ledger but not the statement
  // Purely informational — no bulk action; user needs to open each and decide.
  return (
    <div data-testid="recon-missing-from-statement">
      <div className="text-xs font-semibold uppercase tracking-widest mb-2 text-red-700">
        In your books but not on this statement ({rows.length})
      </div>
      <p className="text-[11px] text-slate-500 mb-2">
        These transactions exist in the ledger for the same period + account but didn't match any line
        on the uploaded statement. Review each — likely a duplicate, a bank correction,
        or a fraud red-flag.
      </p>
      <div className="space-y-1">
        {rows.map(t => (
          <div key={t.id}
               className="rounded border border-red-100 bg-red-50/40 px-3 py-2 flex items-center gap-3"
               data-testid={`recon-missing-row-${t.id}`}>
            <FileText size={14} className="text-red-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="truncate">{t.description}</div>
              <div className="text-[11px] text-slate-500 font-mono-num">
                {t.date}
                {t.category_account_name && <> · {t.category_account_name}</>}
              </div>
            </div>
            <span className={`font-mono-num tabular-nums w-24 text-right ${
              Number(t.amount) < 0 ? "text-red-700" : "text-emerald-700"
            }`}>
              {fmtMoney(t.amount)}
            </span>
            <Link
              to={`/accounting/transactions?highlight=${t.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-cyan-700 hover:underline"
            >
              Open
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function round2(v) { return Math.round(v * 100) / 100; }

function spansMultipleMonths(start, end) {
  if (!start || !end) return false;
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e)) return false;
  return s.getUTCFullYear() !== e.getUTCFullYear() ||
         s.getUTCMonth() !== e.getUTCMonth();
}

// Prefer a "May 2026" label when the period spans a whole calendar month
// (which is now the default). Fall back to the raw date range for legacy
// docs where period_start and period_end don't align to month boundaries.
function formatPeriodLabel(start, end, asOf) {
  const s = start || asOf, e = end || asOf;
  if (!s || !e) return "—";
  const sd = new Date(s), ed = new Date(e);
  if (isNaN(sd) || isNaN(ed)) return `${s} → ${e}`;
  const sameMonth = sd.getUTCFullYear() === ed.getUTCFullYear() &&
                    sd.getUTCMonth() === ed.getUTCMonth();
  const spansFullMonth = sd.getUTCDate() === 1 &&
    ed.getUTCDate() === new Date(ed.getUTCFullYear(), ed.getUTCMonth() + 1, 0).getUTCDate();
  if (sameMonth && spansFullMonth) {
    return sd.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  }
  return `${s} → ${e}`;
}


// ---- Month roster: one row per bank / credit-card account with the
// per-account status + a "Reconcile" button for anything still needing
// work. Powers the one-click reconciliation entry from the Cockpit.
function MonthRosterPanel({ month, roster, loading, onReconcile, selectedAcctId }) {
  const monthLabel = (() => {
    const m = /^(\d{4})-(\d{2})$/.exec(month || "");
    if (!m) return month || "";
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleString("en-US", { month: "long", year: "numeric" });
  })();
  const totals = roster?.totals || {};
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="recon-month-roster">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b bg-slate-50 flex-wrap">
        <div>
          <div className="font-heading font-semibold text-sm">Accounts for {monthLabel}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {loading ? "Loading…" : (
              roster
                ? <>
                    {totals.accounts || 0} account{totals.accounts === 1 ? "" : "s"} ·{" "}
                    <span className="text-emerald-700 font-medium">{totals.reconciled || 0} done</span>
                    {totals.partially_reconciled ? <> · <span className="text-amber-700 font-medium">{totals.partially_reconciled} partial</span></> : null}
                    {totals.needs_reconciliation ? <> · <span className="text-rose-700 font-medium">{totals.needs_reconciliation} to go</span></> : null}
                    {" · "}{totals.txn_total || 0} txns total
                  </>
                : "—"
            )}
          </div>
        </div>
      </div>
      {loading && (
        <div className="p-6 text-center text-xs text-slate-500">
          <Loader2 size={14} className="inline animate-spin mr-1" /> Loading month roster…
        </div>
      )}
      {!loading && roster && (roster.rows || []).length === 0 && (
        <div className="p-6 text-center text-sm text-slate-500">
          No bank or credit-card accounts had activity in {monthLabel}.
        </div>
      )}
      {!loading && roster && (roster.rows || []).length > 0 && (
        <table className="w-full text-sm">
          <thead className="bg-white text-xs uppercase text-slate-500 border-b">
            <tr>
              <th className="px-4 py-2 text-left">Account</th>
              <th className="px-2 py-2 text-right whitespace-nowrap">Txns</th>
              <th className="px-2 py-2 text-right whitespace-nowrap">Cleared</th>
              <th className="px-2 py-2 text-left whitespace-nowrap">Status</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(roster.rows || []).map(r => {
              const needs = r.status === "needs_reconciliation" || r.status === "partially_reconciled";
              return (
                <tr
                  key={r.account_id}
                  data-testid={`recon-roster-row-${r.account_id}`}
                  className={`border-b last:border-b-0 ${selectedAcctId === r.account_id ? "bg-cyan-50/40" : "hover:bg-slate-50"}`}
                >
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{r.account_name}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.account_code}{r.account_type ? ` · ${r.account_type === "liability" ? "credit card / liability" : "bank / asset"}` : ""}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs text-slate-700">
                    {r.txn_count}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs text-slate-700">
                    {r.cleared_count}
                    {r.txn_count > 0 && r.cleared_count < r.txn_count && (
                      <span className="text-rose-600"> / {r.txn_count}</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5">
                    <StatusBadge status={r.status} r={r} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {needs ? (
                      <button
                        onClick={() => onReconcile(r.account_id)}
                        data-testid={`recon-roster-reconcile-${r.account_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        Reconcile <ArrowRight size={12} />
                      </button>
                    ) : r.status === "reconciled" || r.status === "reconciled_no_activity" ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                        <CheckCircle2 size={13} /> Done
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusBadge({ status, r }) {
  if (status === "reconciled") return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-700">
      <CheckCircle2 size={11} /> Reconciled
    </span>
  );
  if (status === "reconciled_no_activity") return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 text-slate-600">
      <CheckCircle2 size={11} /> Reconciled · no activity
    </span>
  );
  if (status === "partially_reconciled") return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800"
          title={r?.reconciliation_period_end ? `Reconciled through ${r.reconciliation_period_end} — month not fully covered` : ""}>
      <CircleAlert size={11} /> Partial
    </span>
  );
  if (status === "no_activity") return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 text-slate-500">
      No activity
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-50 border-rose-200 text-rose-700">
      <AlertTriangle size={11} /> Needs reconciliation
    </span>
  );
}

